import { Badge, Table, Tooltip } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { prvApi } from "#/api/prvApi";
import Pagination from "#/components/buttons/Pagination";
import BaseTable from "#/components/tables/BaseTable";
import { useClient } from "#/hook/useClient";
import { type PrvRun, PrvRunStatus, PrvRunTrigger } from "#/types/prv";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { formatDuration, runDurationSeconds } from "./prvUtils";

export const PRV_RUNS_QUERY_KEY = "prv-runs";

const STATUS_COLOR: Record<PrvRunStatus, string> = {
  [PrvRunStatus.RUNNING]: "blue",
  [PrvRunStatus.COMPLETED]: "green",
  [PrvRunStatus.FAILED]: "red",
};

interface Props {
  clientId: number;
  /** True while the tab knows a run is in progress; the log then refreshes itself. */
  polling: boolean;
}

/** The client's PRV run log, newest first. */
const PrvRunLogTable = ({ clientId, polling }: Props) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data, isFetching } = useQuery({
    queryKey: [PRV_RUNS_QUERY_KEY, clientId, page, limit],
    queryFn: async () => {
      const response = await prvApi.listRuns(clientId, {
        page,
        limit,
        sortBy: "startedAt",
        order: "DESC",
      });
      return response.data;
    },
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.data.some((run) => run.status === PrvRunStatus.RUNNING);
      return polling || hasRunning ? 2000 : false;
    },
  });

  const runs = data?.data ?? [];

  const headers = [
    t("clients.prv.runLog.when"),
    t("clients.prv.runLog.trigger"),
    t("clients.prv.runLog.status"),
    t("clients.prv.runLog.duration"),
    t("clients.prv.runLog.rowsRead"),
    t("clients.prv.runLog.items"),
    t("clients.prv.runLog.stays"),
    t("clients.prv.runLog.result"),
  ];

  const resultOf = (run: PrvRun): string => {
    if (run.status === PrvRunStatus.FAILED) return run.error ?? t("clients.prv.status.failed");
    if (run.status === PrvRunStatus.RUNNING) {
      return run.step ? t(`clients.prv.modal.steps.${run.step}`) : t("clients.prv.modal.starting");
    }
    return run.rowsRead === 0
      ? t("clients.prv.runLog.noNewData")
      : t("clients.prv.status.completed");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="min-h-0 flex-1 overflow-auto">
        <BaseTable
          headers={headers}
          loading={isFetching && runs.length === 0}
          rows={runs.map((run) => (
            <Table.Tr key={run.id}>
              <Table.Td>
                <span className="whitespace-nowrap">
                  {formatDate(run.startedAt, client)}
                  <br />
                  <span className="text-xs text-gray-500">{formatTime(run.startedAt, client)}</span>
                </span>
              </Table.Td>
              <Table.Td>
                <div className="flex flex-col gap-0.5">
                  <Badge
                    variant="outline"
                    color={run.trigger === PrvRunTrigger.MANUAL ? "grape" : "gray"}
                    size="sm"
                  >
                    {t(`clients.prv.trigger.${run.trigger}`)}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {run.triggeredBy?.name ?? t("clients.prv.runLog.scheduler")}
                  </span>
                </div>
              </Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[run.status]} variant="light" size="sm">
                  {t(`clients.prv.status.${run.status}`)}
                </Badge>
              </Table.Td>
              <Table.Td className="tabular-nums">
                {formatDuration(runDurationSeconds(run), t)}
              </Table.Td>
              <Table.Td className="tabular-nums">{run.rowsRead}</Table.Td>
              <Table.Td className="tabular-nums">{run.itemsTouched}</Table.Td>
              <Table.Td className="tabular-nums">{run.staysConfirmed}</Table.Td>
              <Table.Td>
                <Tooltip label={resultOf(run)} withArrow multiline maw={320} disabled={!run.error}>
                  <span
                    className={`block max-w-[16rem] truncate text-sm ${
                      run.status === PrvRunStatus.FAILED ? "text-red-700" : "text-gray-700"
                    }`}
                  >
                    {resultOf(run)}
                  </span>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        />
      </div>
      <div className="flex justify-end">
        <Pagination
          page={page}
          limit={data?.limit ?? limit}
          total={data?.total ?? 0}
          onChange={setPage}
          onLimitChange={(next) => {
            setLimit(next);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default PrvRunLogTable;
